import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, IconButton,
    CircularProgress, useTheme, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Collapse from '@mui/material/Collapse';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import html2pdf from 'html2pdf.js/dist/html2pdf.bundle.min.js';
import DownloadIcon from '@mui/icons-material/Download';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { tokens } from '../../../../theme';
import v2Api from '../../../../utils/v2Api';
import ScoreRings from './components/ScoreRings';
import QuestionSlot from './components/QuestionSlot';
import QuestionNav from './components/QuestionNav';
import getCookies from '../../../../utils/getCookies';
import QuestionDisplay from './components/QuestionDisplay';
import { shouldRevealAnswersToStudent } from './resultUtils';
import { scaleLabel, scaleMax } from './scoreScale';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FlagIcon from '@mui/icons-material/Flag';

// Contiguous group run around list[idx] — scattered group members (legacy or
// hand-reordered tests) label each run by its own span instead of a min-max
// that would overlap other passages.
const contiguousGroupNums = (list, idx) => {
    const q = list[idx];
    if (!q?.group_id) return [];
    let lo = idx, hi = idx;
    while (lo > 0 && list[lo - 1].group_id === q.group_id) lo--;
    while (hi < list.length - 1 && list[hi + 1].group_id === q.group_id) hi++;
    return lo === hi ? [] : list.slice(lo, hi + 1).map(x => x.question_number);
};


pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

// Render at 2x minimum so PDFs stay crisp on Retina / high-DPI screens
const pixelRatio = Math.max(window.devicePixelRatio || 1, 2);

// Simple PDF renderer with own page count
const PdfDoc = ({ file }) => {
    const [pages, setPages] = useState(null);
    return (
        <Document file={file} onLoadSuccess={({ numPages }) => setPages(numPages)}
            loading={<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>}
        >
            {pages && Array.from({ length: pages }, (_, i) => (
                <Page key={i + 1} pageNumber={i + 1} scale={pixelRatio} renderTextLayer={false} renderAnnotationLayer={false} />
            ))}
        </Document>
    );
};

const StudentResultV2 = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { id: courseUuid, courseTestId, sessionId } = useParams();
    const navigate = useNavigate();

    const currentUser = getCookies("_auth_state");
    const userRole = currentUser ? currentUser.split(',')[2]?.split(':')[1]?.split('"')[1] : null;
    const isAdmin = userRole === 'admin';

    const [loading, setLoading] = useState(true);
    const [session, setSession] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [showExplanations, setShowExplanations] = useState(false);
    const [showScores, setShowScores] = useState(true);
    const [editingPart, setEditingPart] = useState(null);
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('v2ResultViewMode') || 'all');
    const [exporting, setExporting] = useState(false);

    // Split pane
    const [dividerPosition, setDividerPosition] = useState(6);
    const rightPaneRef = useRef(null);

    const handleMouseDown = useCallback(() => {
        const handleMouseMove = (e) => {
            if (rightPaneRef.current) {
                setDividerPosition(Math.max(2, Math.min(8, (e.clientX / window.innerWidth) * 10)));
            }
        };
        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, []);

    const fetchSession = () => {
        v2Api.get(`/sessions/${sessionId}`)
            .then(res => setSession(res.data.data))
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchSession();
    }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Kill outer .content scroll via CSS class — auto-removed when component unmounts
    useEffect(() => {
        const style = document.createElement('style');
        style.id = 'v2-result-no-outer-scroll';
        style.textContent = 'main.content { overflow: hidden !important; }';
        document.head.appendChild(style);
        return () => style.remove();
    }, []);

    // Block print + kill outer scroll for Question UI mode
    useEffect(() => {
        const style = document.createElement('style');
        style.id = 'v2-result-print-block';
        style.textContent = '@media print { body * { visibility: hidden !important; } }';
        document.head.appendChild(style);

        const prevOverflows = [];
        const root = document.getElementById('v2-result-ui-root');
        if (root) {
            let el = root.parentElement;
            while (el && el !== document.body) {
                if (el.scrollHeight > el.clientHeight) {
                    prevOverflows.push({ el, prev: el.style.overflow });
                    el.style.overflow = 'hidden';
                }
                el = el.parentElement;
            }
        }

        return () => {
            style.remove();
            prevOverflows.forEach(({ el, prev }) => { el.style.overflow = prev; });
        };
    }, [session]);

    // ── Question UI results infrastructure (scroll sync, keyboard, etc.) ──
    const resultScrollRef = useRef(null);
    const resultProgrammaticUntil = useRef(0);
    // Defined early but safe — test is undefined until session loads, optional chaining handles it
    const isQuestionUi = session?.v2_course_test?.v2_test?.use_question_ui && !showExplanations;

    // Scroll listener — sync currentQuestionIndex with scroll in "all" mode
    useEffect(() => {
        if (!isQuestionUi || viewMode !== 'all') return;
        const container = resultScrollRef.current;
        if (!container) return;

        let rafId = null;
        const handleScroll = () => {
            if (rafId) return;
            rafId = requestAnimationFrame(() => {
                rafId = null;
                if (Date.now() < resultProgrammaticUntil.current) return;
                const cards = container.querySelectorAll('[data-qidx]');
                const containerTop = container.getBoundingClientRect().top;
                let closest = 0, closestDist = Infinity;
                cards.forEach((card) => {
                    const dist = Math.abs(card.getBoundingClientRect().top - containerTop);
                    const idx = parseInt(card.dataset.qidx, 10);
                    if (dist < closestDist) { closestDist = dist; closest = idx; }
                });
                setCurrentQuestionIndex(closest);
            });
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => { container.removeEventListener('scroll', handleScroll); if (rafId) cancelAnimationFrame(rafId); };
    }, [isQuestionUi, viewMode, currentSectionIndex]);

    // Derive questions early for hooks (safe — returns [] when session not loaded)
    const _test = session?.v2_course_test?.v2_test;
    const _sections = _test?.v2_test_sections || [];
    const _curQuestions = _sections[currentSectionIndex]?.v2_test_questions || [];

    // Programmatic scroll helper
    const resultProgrammaticScroll = (idx) => {
        setCurrentQuestionIndex(idx);
        resultProgrammaticUntil.current = Date.now() + 100;
        const container = resultScrollRef.current;
        if (container) {
            const el = document.getElementById(`result-card-${_curQuestions[idx]?.id}`);
            if (el) {
                const containerRect = container.getBoundingClientRect();
                const elRect = el.getBoundingClientRect();
                container.scrollTop = elRect.top - containerRect.top + container.scrollTop;
            }
        }
    };

    // Scroll to current question on mode switch to "all"
    useEffect(() => {
        if (!isQuestionUi || viewMode !== 'all') return;
        const frame = requestAnimationFrame(() => {
            resultProgrammaticUntil.current = Date.now() + 300;
            const q = _curQuestions[currentQuestionIndex];
            if (!q) return;
            const el = document.getElementById(`result-card-${q.id}`);
            const container = resultScrollRef.current;
            if (el && container) container.scrollTop = el.offsetTop;
        });
        return () => cancelAnimationFrame(frame);
    }, [viewMode]);

    // Keyboard nav — left/right for single, up/down for all (Question UI only)
    useEffect(() => {
        if (!isQuestionUi) return;
        const handler = (e) => {
            if (viewMode === 'single') {
                if (e.key === 'ArrowLeft') { e.preventDefault(); setCurrentQuestionIndex(i => Math.max(0, i - 1)); }
                if (e.key === 'ArrowRight') { e.preventDefault(); setCurrentQuestionIndex(i => Math.min(_curQuestions.length - 1, i + 1)); }
            } else {
                if (e.key === 'ArrowUp') { e.preventDefault(); resultProgrammaticScroll(Math.max(0, currentQuestionIndex - 1)); }
                if (e.key === 'ArrowDown') { e.preventDefault(); resultProgrammaticScroll(Math.min(_curQuestions.length - 1, currentQuestionIndex + 1)); }
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    });

    // Poll while FRQ grading is pending (admin only — students see results when fully done)
    const hasPending = (session?.v2_student_part_scores || []).some(ps => ps.grading_status === 'pending' || ps.grading_status === 'grading');
    useEffect(() => {
        if (!hasPending || !isAdmin) return;
        const interval = setInterval(fetchSession, 5000);
        return () => clearInterval(interval);
    }, [hasPending, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSavePartScore = async () => {
        if (!editingPart) return;
        try {
            await v2Api.patch(`/part-scores/${editingPart.id}`, {
                score: parseFloat(editingPart.score),
                feedback_text: editingPart.feedback_text,
            });
            // Update local state
            setSession(prev => ({
                ...prev,
                v2_student_part_scores: prev.v2_student_part_scores.map(ps =>
                    ps.id === editingPart.id
                        ? { ...ps, score: parseFloat(editingPart.score), feedback_text: editingPart.feedback_text, grading_status: 'manual' }
                        : ps
                ),
            }));
            setEditingPart(null);
        } catch (err) {
            console.error('Failed to save part score:', err);
        }
    };

    const handleExportPdf = async () => {
        setExporting(true);
        try {
            const title = session?.v2_course_test?.title || test?.title || 'Test Results';
            const sa = session?.v2_student_answers || [];
            const ps = session?.v2_student_part_scores || [];
            const saMap = {};
            sa.forEach(a => { saMap[a.question_id] = a; });
            const psMap = {};
            const wholeQuestionScoreMap = {}; // question_id → score row with part_id === null
            ps.forEach(p => {
                if (p.part_id != null) psMap[p.part_id] = p;
                else wholeQuestionScoreMap[p.question_id] = p;
            });

            // Choice-group selections from session.metadata — used to filter out non-selected questions.
            const pdfChoiceGroupSelections = (() => {
                const raw = session?.metadata?.choice_group_selections;
                if (!raw || typeof raw !== 'object') return null;
                const normalized = {};
                for (const [k, v] of Object.entries(raw)) {
                    normalized[k] = typeof v === 'number' ? v : parseInt(v, 10);
                }
                return Object.keys(normalized).length > 0 ? normalized : null;
            })();

            // Small helper: turn feedback text containing \(…\), \[…\], $…$ etc into HTML with KaTeX-rendered math.
            // Mirrors QuestionRender's logic so PDFs render math instead of literal LaTeX.
            const renderLatexInText = (text) => {
                if (!text) return '';
                let s = String(text)
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;');
                // Un-escape our own escaped delimiters before extracting math
                s = s.replace(/\\\\\[/g, '\\[').replace(/\\\\\]/g, '\\]').replace(/\\\\\(/g, '\\(').replace(/\\\\\)/g, '\\)');
                const render = (latex, displayMode) => {
                    try {
                        return katex.renderToString(latex, { displayMode, throwOnError: false, strict: false });
                    } catch {
                        return `<span style="color:#cc0000;">${latex}</span>`;
                    }
                };
                s = s.replace(/\\\[([\s\S]*?)\\\]/g, (_, m) => render(m, true));
                s = s.replace(/\$\$([\s\S]+?)\$\$/g, (_, m) => render(m, true));
                s = s.replace(/\\\(([\s\S]*?)\\\)/g, (_, m) => render(m, false));
                s = s.replace(/\$([^$\n]+)\$/g, (_, m) => render(m, false));
                // Convert newlines to <br> for readable prose
                s = s.replace(/\n/g, '<br/>');
                return s;
            };

            // Parts may be flat (from getSessionWithResults) or pre-nested (from getTestById)
            const getSubs = (part) => part.subparts || part.v2_question_parts || part.children || [];

            // Build tree from flat parts list (when parts have parent_id but no .subparts)
            const buildTree = (flatParts) => {
                if (!flatParts?.length) return flatParts;
                // If already nested (has .subparts), return as-is
                if (flatParts[0].subparts) return flatParts;
                const hasParentIds = flatParts.some(p => p.parent_id);
                if (!hasParentIds) return flatParts;
                const byId = {};
                flatParts.forEach(p => { byId[p.id] = { ...p, children: [] }; });
                const roots = [];
                flatParts.forEach(p => {
                    if (p.parent_id && byId[p.parent_id]) byId[p.parent_id].children.push(byId[p.id]);
                    else roots.push(byId[p.id]);
                });
                const sort = (list) => { list.sort((a, b) => (a.part_order || 0) - (b.part_order || 0)); list.forEach(p => sort(p.children)); };
                sort(roots);
                return roots;
            };

            const renderAnswerHtml = (answer) => {
                if (!answer) return '';
                if (Array.isArray(answer)) {
                    let html = `<div style="margin-top:4px;font-size:12px;">`;
                    answer.forEach((url, i) => {
                        html += `<a href="${url}" target="_blank" style="color:#4a90d9;margin-right:8px;">View image ${i + 1}</a>`;
                    });
                    return html + `</div>`;
                }
                return `<div style="margin-top:4px;padding:4px 8px;background:#f5f5f5;border-radius:3px;white-space:pre-wrap;font-size:12px;">${answer}</div>`;
            };

            const renderPartHtml = (parts, frqAnswers, depth = 0, prefix = '') => {
                return parts.map(part => {
                    const subs = getSubs(part);
                    const hasKids = subs.length > 0;
                    const compositeLabel = prefix + part.part_label;
                    const score = psMap[part.id];
                    const answer = frqAnswers[compositeLabel] ?? frqAnswers[part.part_label] ?? '';
                    const ml = depth * 16;
                    let html = `<div style="margin-left:${ml}px;margin-top:4px;padding:6px;border:1px solid #ddd;border-radius:4px;">`;
                    html += `<b>Part ${part.part_label}</b>`;
                    if (!hasKids && score) {
                        const icon = score.score >= score.max_score ? '&#9989;' : score.score > 0 ? '&#9888;' : '&#10060;';
                        html += ` &mdash; ${score.score}/${score.max_score} ${icon}`;
                    }
                    // Show answer on any node that has one (handles old data on parents + new data on leaves)
                    if (answer) html += renderAnswerHtml(answer);
                    if (!hasKids && score?.feedback_text) {
                        html += `<div style="margin-top:4px;padding:4px 8px;border-left:3px solid #4a90d9;background:#f0f7ff;border-radius:0 3px 3px 0;font-size:12px;"><i>Feedback:</i> ${renderLatexInText(score.feedback_text)}</div>`;
                    }
                    html += '</div>';
                    if (hasKids) html += renderPartHtml(subs, frqAnswers, depth + 1, compositeLabel + '.');
                    return html;
                }).join('');
            };

            // Score summary
            const mcqScore = session?.mcq_score ?? 0;
            const mcqTotal = session?.mcq_total ?? 0;
            let frqEarned = 0, frqMax = 0;
            ps.forEach(p => { frqEarned += (p.score ?? 0); frqMax += (p.max_score ?? 0); });

            let html = `<div style="font-family:Arial,sans-serif;padding:16px;max-width:700px;">`;
            html += `<h2 style="margin-bottom:4px;">${title}</h2>`;
            html += `<p style="color:#666;margin-top:0;">Student: ${session?.student ? `${session.student.first_name} ${session.student.last_name}` : (session?.student_uuid || 'N/A')}</p>`;

            // Score breakdown
            html += `<table style="border-collapse:collapse;margin-bottom:16px;"><tr>`;
            if (mcqTotal > 0) html += `<td style="padding:8px 16px;border:1px solid #ddd;text-align:center;"><b>MCQ</b><br/>${mcqScore}/${mcqTotal}</td>`;
            if (frqMax > 0) html += `<td style="padding:8px 16px;border:1px solid #ddd;text-align:center;"><b>FRQ</b><br/>${frqEarned}/${frqMax}</td>`;
            if (mcqTotal > 0 && frqMax > 0) html += `<td style="padding:8px 16px;border:1px solid #ddd;text-align:center;"><b>Total</b><br/>${mcqScore + frqEarned}/${mcqTotal + frqMax}</td>`;
            if (session?.scaled_score != null) html += `<td style="padding:8px 16px;border:1px solid #ddd;text-align:center;"><b>${scaleLabel(test)}</b><br/>${session.scaled_score}</td>`;
            html += `</tr></table>`;

            // Per-question results
            const sects = test?.v2_test_sections || [];
            sects.forEach(section => {
                html += `<h3 style="margin-bottom:8px;">${section.section_name || 'Section'}</h3>`;
                (section.v2_test_questions || []).forEach(q => {
                    // Hide non-selected choice-group questions (matches on-screen behavior).
                    if (q.choice_group && pdfChoiceGroupSelections) {
                        const sel = pdfChoiceGroupSelections[q.choice_group];
                        if (sel != null && sel !== q.id) return;
                    }

                    const answer = saMap[q.id];
                    const isMcq = ['mcq', 'multi_answer', 'grid_in', 'sa'].includes(q.question_type);
                    html += `<div style="margin-bottom:8px;padding:8px;border:1px solid #ddd;border-radius:4px;">`;
                    html += `<b>Q${q.question_number || '?'}</b> <span style="color:#888;font-size:12px;">${(q.question_type || '').toUpperCase()}</span>`;

                    if (isMcq) {
                        const blank = !String(answer?.answer_text ?? '').trim();
                        const icon = answer?.is_correct ? '&#9989;'
                            : answer?.is_correct === false ? (blankIsPriced && blank ? '<span style="color:#888;">(blank)</span>' : '&#10060;')
                            : '';
                        html += `<div style="margin-top:4px;">Answer: <b>${answer?.answer_text || '—'}</b>`;
                        if (q.question_type === 'mcq' && q.answer) html += ` &nbsp; Correct: <b>${q.answer}</b>`;
                        html += ` ${icon}</div>`;
                    } else {
                        // FRQ
                        let frqAnswers = {};
                        try { frqAnswers = answer?.answer_text ? JSON.parse(answer.answer_text) : {}; } catch { if (answer?.answer_text) frqAnswers = { _raw: answer.answer_text }; }
                        if (frqAnswers._raw) {
                            html += `<div style="margin-top:4px;padding:4px 8px;background:#f5f5f5;border-radius:3px;white-space:pre-wrap;font-size:12px;">${frqAnswers._raw}</div>`;
                        }
                        const parts = buildTree(q.v2_question_parts || []);
                        if (parts.length > 0) {
                            html += renderPartHtml(parts, frqAnswers);
                        } else {
                            // Zero-part FRQ — pull score + feedback from the null-part_id row.
                            const wps = wholeQuestionScoreMap[q.id];
                            if (wps) {
                                const icon = wps.score >= wps.max_score ? '&#9989;' : wps.score > 0 ? '&#9888;' : '&#10060;';
                                const scoreDisplay = (wps.grading_status === 'pending' || wps.grading_status === 'grading')
                                    ? '—'
                                    : `${wps.score ?? '?'}/${wps.max_score}`;
                                html += `<div style="margin-top:6px;padding:6px;border:1px solid #ddd;border-radius:4px;font-size:12px;"><b>Score</b> &mdash; ${scoreDisplay} ${icon}`;
                                if (wps.feedback_text) {
                                    html += `<div style="margin-top:4px;padding:4px 8px;border-left:3px solid #4a90d9;background:#f0f7ff;border-radius:0 3px 3px 0;"><i>Feedback:</i> ${renderLatexInText(wps.feedback_text)}</div>`;
                                }
                                html += `</div>`;
                            }
                        }
                    }
                    html += `</div>`;
                });
            });
            html += `</div>`;

            // Render to PDF
            const container = document.createElement('div');
            container.style.cssText = 'position:absolute;left:0;top:0;width:800px;z-index:-1;opacity:0;pointer-events:none;';
            container.innerHTML = html;
            document.body.appendChild(container);

            await new Promise(r => setTimeout(r, 200));

            await html2pdf().set({
                margin: [10, 10, 10, 10],
                filename: `${title.replace(/[^a-zA-Z0-9]/g, '_')}_Results.pdf`,
                image: { type: 'jpeg', quality: 0.95 },
                html2canvas: { scale: 2, useCORS: true, windowWidth: 800 },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
            }).from(container.firstElementChild).save();

            document.body.removeChild(container);
        } catch (err) {
            console.error('PDF export error:', err);
        } finally {
            setExporting(false);
        }
    };

    // Derive data (safe even if session is null)
    const test = session?.v2_course_test?.v2_test;
    // AMC-style profiles pay for a blank, so the question cards mark a blank differently from a
    // wrong answer. Everything else keeps today's red icon for both.
    const blankIsPriced = !!test?.v2_scoring_profile?.metadata?.answer_points;
    const gradesReleased = test?.release_grades !== false;
    // Vocab tests reveal the correct answer to students once grades are released
    // (admins always see it). Other tests keep the answer key hidden from students.
    const revealCorrectAnswers = shouldRevealAnswersToStudent(test, gradesReleased);
    const studentAnswers = session?.v2_student_answers || [];
    const partScores = session?.v2_student_part_scores || [];
    const answerMap = {};
    studentAnswers.forEach(a => {
        // Strip score data from answers when grades not released (non-admin)
        if (!isAdmin && !gradesReleased) {
            answerMap[a.question_id] = { ...a, is_correct: null, points_earned: null };
        } else {
            answerMap[a.question_id] = a;
        }
    });
    const frqPending = partScores.some(ps => ps.grading_status === 'pending' || ps.grading_status === 'grading');
    const partScoreById = {};
    const wholeQuestionScoreByQid = {}; // for zero-part FRQ questions (part_id === null)
    // Persisted choice_group_selections — used to hide non-selected choice-group questions.
    // Missing on legacy sessions — in that case QuestionSlot falls back to showing everything.
    const choiceGroupSelections = (() => {
        const raw = session?.metadata?.choice_group_selections;
        if (!raw || typeof raw !== 'object') return null;
        const normalized = {};
        for (const [k, v] of Object.entries(raw)) {
            normalized[k] = typeof v === 'number' ? v : parseInt(v, 10);
        }
        return normalized;
    })();
    // Students don't see partial FRQ scores — only show when fully graded
    // Also hide all scores if grades not released
    if (isAdmin || (!frqPending && gradesReleased)) {
        partScores.forEach(ps => {
            if (ps.part_id != null) partScoreById[ps.part_id] = ps;
            else wholeQuestionScoreByQid[ps.question_id] = ps;
        });
    }

    const sections = test?.v2_test_sections || [];
    const curQuestions = sections[currentSectionIndex]?.v2_test_questions || [];

    // Navigation helpers
    const goPrev = () => {
        if (currentQuestionIndex > 0) setCurrentQuestionIndex(currentQuestionIndex - 1);
        else if (currentSectionIndex > 0) {
            const prevQs = sections[currentSectionIndex - 1]?.v2_test_questions || [];
            setCurrentSectionIndex(currentSectionIndex - 1);
            setCurrentQuestionIndex(prevQs.length - 1);
        }
    };
    const goNext = () => {
        if (currentQuestionIndex < curQuestions.length - 1) setCurrentQuestionIndex(currentQuestionIndex + 1);
        else if (currentSectionIndex < sections.length - 1) { setCurrentSectionIndex(currentSectionIndex + 1); setCurrentQuestionIndex(0); }
    };
    const isFirst = currentSectionIndex === 0 && currentQuestionIndex === 0;
    const isLast = currentSectionIndex === sections.length - 1 && currentQuestionIndex === curQuestions.length - 1;

    // Keyboard nav
    useEffect(() => {
        if (viewMode !== 'single') return;
        const handler = (e) => {
            if (e.key === 'ArrowLeft') { e.preventDefault(); document.activeElement?.blur(); goPrev(); }
            if (e.key === 'ArrowRight') { e.preventDefault(); document.activeElement?.blur(); goNext(); }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    });

    if (loading || !session) {
        return (
            <Box sx={{
                display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh',
                '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
                animation: 'fadeIn 0.3s ease-in',
            }}>
                <CircularProgress />
            </Box>
        );
    }

    // Score calculations
    const mcqScore = session.mcq_score ?? 0;
    const mcqTotal = session.mcq_total ?? 0;
    let frqEarned = 0, frqMax = 0;
    // Total from question part definitions, accounting for choice groups
    const cgTotals = {}, cgScores = {};
    for (const sec of sections) {
        for (const q of (sec.v2_test_questions || [])) {
            if (['mcq', 'multi_answer', 'grid_in', 'sa'].includes(q.question_type)) continue;
            const qParts = q.v2_question_parts || [];
            const leafParts = qParts.filter(p => !qParts.some(c => c.parent_id === p.id));
            let qTotal = 0, qScore = 0;
            for (const part of leafParts) {
                qTotal += (part.max_points || 0);
                const ps = partScores.find(ps => ps.part_id === part.id);
                if (ps) qScore += (ps.score ?? 0);
            }
            if (leafParts.length === 0) {
                // Zero-part FRQ — score lives on the part_id=null row.
                qTotal = q.max_points || 0;
                const wps = partScores.find(ps => ps.question_id === q.id && ps.part_id == null);
                if (wps) qScore = wps.score ?? 0;
            }
            if (q.choice_group) {
                cgTotals[q.choice_group] = Math.max(cgTotals[q.choice_group] || 0, qTotal);
                if (partScores.some(ps => ps.question_id === q.id)) cgScores[q.choice_group] = qScore;
            } else {
                frqMax += qTotal;
                frqEarned += qScore;
            }
        }
    }
    for (const g of Object.keys(cgTotals)) {
        frqMax += cgTotals[g];
        if (cgScores[g] != null) frqEarned += cgScores[g];
    }
    const hasFrq = frqMax > 0;

    return (
        <Box sx={{
            display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', mx: 2,
            '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
            animation: 'fadeIn 0.3s ease-in',
        }}>
            {/* Header */}
            <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton size="small" onClick={() => navigate(
                    (userRole === 'admin' || userRole === 'teacher') ? `/classrooms/${courseUuid}/test-v2/${courseTestId}/grading` : `/classrooms/${courseUuid}/tests`
                )}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
                    {session.v2_course_test?.title || test?.title} — Results
                </Typography>
                {/* Explanations are teacher/admin material (2026-08 admin decision) —
                    the server also strips the URL from student payloads */}
                {test?.explanations_file_url && isAdmin && (
                    <Button size="small" variant="outlined" onClick={() => setShowExplanations(p => !p)}
                        sx={{ fontSize: 11, textTransform: 'none', borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'text.secondary' } }}
                    >
                        {showExplanations ? 'Show Test PDF' : 'Show Explanations'}
                    </Button>
                )}
                {(isAdmin || gradesReleased) && (
                    <Button size="small" variant="outlined" onClick={() => setShowScores(p => !p)}
                        sx={{ fontSize: 11, textTransform: 'none', borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'text.secondary' } }}
                    >
                        {showScores ? 'Hide Scores' : 'Show Scores'}
                    </Button>
                )}
                {userRole !== 'teacher' && (isAdmin || gradesReleased) && (
                    <Button size="small" variant="outlined" onClick={handleExportPdf} disabled={exporting}
                        startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon sx={{ fontSize: 16 }} />}
                        sx={{ fontSize: 11, textTransform: 'none', borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'text.secondary' } }}
                    >
                        {exporting ? 'Exporting...' : 'Download'}
                    </Button>
                )}
            </Box>

            {/* Score summary — collapsible (hidden for students when grades not released) */}
            <Collapse in={showScores && (isAdmin || gradesReleased)} timeout={250}>
            <Box>
                {(() => {
                    // Build per-section scores
                    const sectionScores = sections.map(section => {
                        const qs = section.v2_test_questions || [];
                        let score = 0, total = 0, pending = false;

                        // Track choice groups: only count max_points once per group
                        const choiceGroupTotals = {}; // group → max points (take highest)
                        const choiceGroupScores = {}; // group → best score from answered question

                        for (const q of qs) {
                            const sa = answerMap[q.id];
                            const isMcq = ['mcq', 'multi_answer', 'grid_in', 'sa'].includes(q.question_type);

                            if (isMcq) {
                                total += (q.max_points || 1);
                                if (sa?.is_correct) score += (sa.points_earned || 1);
                            } else {
                                const questionParts = q.v2_question_parts || [];
                                const leafParts = questionParts.filter(p => !questionParts.some(c => c.parent_id === p.id));
                                let qTotal = 0, qScore = 0, qPending = false;
                                for (const part of leafParts) {
                                    qTotal += (part.max_points || 0);
                                    const ps = partScores.find(ps => ps.part_id === part.id);
                                    if (ps) {
                                        qScore += (ps.score || 0);
                                        if (ps.grading_status === 'pending' || ps.grading_status === 'grading') qPending = true;
                                    }
                                }
                                if (leafParts.length === 0) {
                                    // Zero-part FRQ — score lives on the part_id=null row.
                                    qTotal = q.max_points || 0;
                                    const wps = partScores.find(ps => ps.question_id === q.id && ps.part_id == null);
                                    if (wps) {
                                        qScore = wps.score || 0;
                                        if (wps.grading_status === 'pending' || wps.grading_status === 'grading') qPending = true;
                                    }
                                }

                                if (q.choice_group) {
                                    const g = q.choice_group;
                                    choiceGroupTotals[g] = Math.max(choiceGroupTotals[g] || 0, qTotal);
                                    // Use the question the student actually answered (has part scores)
                                    const hasAnswer = partScores.some(ps => ps.question_id === q.id);
                                    if (hasAnswer) {
                                        choiceGroupScores[g] = { score: qScore, pending: qPending };
                                    }
                                } else {
                                    total += qTotal;
                                    score += qScore;
                                    if (qPending) pending = true;
                                }
                            }
                        }

                        // Add choice group contributions (once per group)
                        for (const g of Object.keys(choiceGroupTotals)) {
                            total += choiceGroupTotals[g];
                            if (choiceGroupScores[g]) {
                                score += choiceGroupScores[g].score;
                                if (choiceGroupScores[g].pending) pending = true;
                            }
                        }

                        // For students: hide section entirely if FRQ is still pending
                        if (!isAdmin && pending) {
                            return { name: section.section_name || `Section`, score: 0, total, pending: true };
                        }

                        // Admin pending text
                        let adminPendingText = null;
                        if (isAdmin && pending) {
                            const qMap = {};
                            const qParts = partScores.filter(ps => qs.some(q => q.id === ps.question_id));
                            qParts.forEach(ps => {
                                if (!qMap[ps.question_id]) qMap[ps.question_id] = { total: 0, done: 0 };
                                qMap[ps.question_id].total++;
                                if (ps.grading_status === 'completed' || ps.grading_status === 'manual') qMap[ps.question_id].done++;
                            });
                            const qVals = Object.values(qMap);
                            adminPendingText = `${qVals.filter(q => q.done === q.total).length}/${qVals.length} questions`;
                        }

                        // Apply display score override for students
                        const ds = session.display_scores;
                        const displayScore = !isAdmin && ds?.sections?.[section.id] != null ? ds.sections[section.id] : null;

                        return {
                            name: section.section_name || `Section`,
                            score: displayScore != null ? displayScore : score,
                            total, pending, adminPendingText,
                        };
                    });

                    const ds = session.display_scores;

                    return (
                        <ScoreRings
                            sectionScores={sectionScores}
                            compositeScore={isAdmin || !frqPending ? session.composite_score : null}
                            scaledScore={!isAdmin && ds?.ap_score != null ? ds.ap_score :
                                (isAdmin || !frqPending ? session.scaled_score : null)}
                            scaledLabel={scaleLabel(test)}
                            scaledMax={scaleMax(test)}
                            // Alternates are derived from the stored section scores. When an admin
                            // has overridden the headline via display_scores, the derived figures
                            // no longer belong to it — showing both would contradict each other,
                            // so suppress them rather than mix an override with computed values.
                            alternates={!isAdmin && ds?.ap_score != null ? null : session.composites}
                            // AMC: the stored correct/blank/wrong split behind the total. Suppressed
                            // under a student-facing override for the same reason as alternates.
                            breakdown={!isAdmin && ds?.ap_score != null ? null : (session.section_scores?.AMC || null)}
                            isAdmin={isAdmin}
                        />
                    );
                })()}
                {isAdmin && test?.test_type_id && !test?.scoring_profile_id && !frqPending && !session.scaled_score && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, pb: 1 }}>
                        <Tooltip title="No scoring profile assigned to this test. A scaled score cannot be calculated.">
                            <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 16 }} />
                        </Tooltip>
                        <Typography variant="caption" color="warning.main">No scoring profile</Typography>
                    </Box>
                )}
            </Box>
            </Collapse>

            {isQuestionUi ? (
                /* ── Question UI results mode ── */
                <Box id="v2-result-ui-root" sx={{
                    flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
                    '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
                }}>
                    {/* Shared nav — full width */}
                    <QuestionNav
                        sections={sections}
                        currentSectionIndex={currentSectionIndex}
                        onSectionNavigate={(idx) => { setCurrentSectionIndex(idx); setCurrentQuestionIndex(0); }}
                        answers={new Map(studentAnswers.map(a => [a.question_id, { answer_text: a.answer_text }]))}
                        viewMode={viewMode}
                        onToggleViewMode={() => { const next = viewMode === 'single' ? 'all' : 'single'; setViewMode(next); localStorage.setItem('v2ResultViewMode', next); }}
                        currentQuestionIndex={currentQuestionIndex}
                        sectionTotal={curQuestions.length}
                        isFirst={isFirst}
                        isLast={isLast}
                        onPrev={goPrev}
                        onNext={goNext}
                        onProgressClick={(idx) => setCurrentQuestionIndex(idx)}
                        flaggedQuestions={curQuestions
                            .map((q, idx) => answerMap[q.id]?.flagged ? { id: q.id, index: idx } : null)
                            .filter(Boolean)}
                        onFlagClick={(idx) => setCurrentQuestionIndex(idx)}
                        useQuestionUi
                        questionResults={curQuestions.map(q => {
                            const sa = answerMap[q.id];
                            const isMcq = ['mcq', 'multi_answer', 'grid_in', 'sa'].includes(q.question_type);
                            if (!sa?.answer_text) return { status: 'unanswered' };
                            if (isMcq) return { status: sa.is_correct === true ? 'correct' : sa.is_correct === false ? 'wrong' : 'pending' };
                            const qParts = (partScores || []).filter(ps => ps.question_id === q.id);
                            if (qParts.length === 0) return { status: 'pending' };
                            if (qParts.some(ps => ps.grading_status === 'pending' || ps.grading_status === 'grading')) return { status: 'pending' };
                            const earned = qParts.reduce((s, ps) => s + (ps.score || 0), 0);
                            const max = qParts.reduce((s, ps) => s + (ps.max_score || 0), 0);
                            if (earned >= max) return { status: 'correct' };
                            if (earned > 0) return { status: 'partial' };
                            return { status: 'wrong' };
                        })}
                    />

                    {curQuestions.length === 0 ? (
                        /* Empty section — placeholder instead of a stuck blank pane */
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                                No questions in this section — use the section tabs above to continue.
                            </Typography>
                        </Box>
                    ) : viewMode === 'single' ? (
                        /* Single mode — split pane: question left, result right */
                        <Box key="single" sx={{ display: 'flex', flex: 1, overflow: 'hidden', animation: 'fadeIn 0.2s ease-out' }}>
                            <Box sx={{ flex: dividerPosition, overflow: 'auto', p: 3, userSelect: 'none', WebkitUserSelect: 'none' }}
                                onContextMenu={(e) => e.preventDefault()}>
                                {curQuestions[currentQuestionIndex] && (() => {
                                    const curQ = curQuestions[currentQuestionIndex];
                                    const groups = sections[currentSectionIndex]?.v2_question_groups || [];
                                    const group = curQ.group_id ? groups.find(g => g.id === curQ.group_id) : null;
                                    const mates = contiguousGroupNums(curQuestions, currentQuestionIndex);
                                    const groupLabel = mates.length > 1 ? `Shared passage — Questions ${Math.min(...mates)}–${Math.max(...mates)}` : null;
                                    return (
                                        <Box key={`${currentSectionIndex}-${currentQuestionIndex}`}
                                            sx={{ '@keyframes fadeSlideIn': { from: { opacity: 0, transform: 'translateX(12px)' }, to: { opacity: 1, transform: 'translateX(0)' } },
                                                animation: 'fadeSlideIn 0.15s ease-out' }}>
                                            <QuestionDisplay question={curQ} group={group} groupLabel={groupLabel} />
                                        </Box>
                                    );
                                })()}
                            </Box>
                            <Box onMouseDown={handleMouseDown} sx={{ width: 6, cursor: 'col-resize', bgcolor: colors.grey[800], '&:hover': { bgcolor: colors.blueAccent[700] }, flexShrink: 0 }} />
                            <Box ref={rightPaneRef} sx={{ flex: 10 - dividerPosition, overflow: 'auto', px: 2, py: 1, minWidth: 300 }}>
                                {curQuestions[currentQuestionIndex] && (
                                    <Box key={`${currentSectionIndex}-${currentQuestionIndex}`}
                                        sx={{ '@keyframes fadeSlideIn': { from: { opacity: 0, transform: 'translateX(12px)' }, to: { opacity: 1, transform: 'translateX(0)' } },
                                            animation: 'fadeSlideIn 0.15s ease-out' }}>
                                        <QuestionSlot
                                            question={curQuestions[currentQuestionIndex]}
                                            allQuestionsInSection={curQuestions}
                                            readOnly verticalOptions revealCorrectAnswers={revealCorrectAnswers}
                                            studentAnswer={answerMap[curQuestions[currentQuestionIndex].id]}
                                            partScoreById={partScoreById}
                                            wholeQuestionScoreByQid={wholeQuestionScoreByQid}
                                            choiceGroupSelections={choiceGroupSelections}
                                            isAdmin={isAdmin}
                                            blankIsPriced={blankIsPriced}
                                            editingPart={editingPart}
                                            setEditingPart={setEditingPart}
                                            onSavePartScore={handleSavePartScore}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    ) : (
                        /* All mode — paired rows + vertical progress sidebar */
                        <Box key="all" sx={{ display: 'flex', flex: 1, overflow: 'hidden', animation: 'fadeIn 0.2s ease-out' }}>
                            {/* Scrollable question list */}
                            <Box ref={resultScrollRef} sx={{
                                flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3,
                                '&::-webkit-scrollbar': { display: 'none' },
                                msOverflowStyle: 'none', scrollbarWidth: 'none',
                            }}>
                                {curQuestions.map((q, idx) => {
                                    const groups = sections[currentSectionIndex]?.v2_question_groups || [];
                                    // Shared passage renders only on the FIRST question of its group
                                    // run — consecutive group-mates show just their stem/options.
                                    const group = q.group_id && curQuestions[idx - 1]?.group_id !== q.group_id
                                        ? groups.find(g => g.id === q.group_id) : null;
                                    const mates = group ? contiguousGroupNums(curQuestions, idx) : [];
                                    const groupLabel = mates.length > 1 ? `Shared passage — Questions ${Math.min(...mates)}–${Math.max(...mates)}` : null;
                                    return (
                                        <Box key={q.id} id={`result-card-${q.id}`} data-qidx={idx} sx={{
                                            display: 'flex', flexWrap: 'wrap', mb: idx < curQuestions.length - 1 ? 3 : 0,
                                            alignItems: 'stretch', border: '1px solid', borderColor: colors.grey[700], borderRadius: 1, overflow: 'hidden',
                                        }}>
                                            <Box sx={{ flex: '1 1 400px', minWidth: 0, p: 2.5, userSelect: 'none', WebkitUserSelect: 'none' }}
                                                onContextMenu={(e) => e.preventDefault()}>
                                                <QuestionDisplay question={q} group={group} groupLabel={groupLabel} />
                                            </Box>
                                            <Box sx={{ width: '1px', bgcolor: colors.grey[700], flexShrink: 0 }} />
                                            <Box sx={{ flex: '1 1 300px', minWidth: 0, p: 2.5 }}>
                                                <QuestionSlot
                                                    question={q}
                                                    allQuestionsInSection={curQuestions}
                                                    readOnly verticalOptions revealCorrectAnswers={revealCorrectAnswers} noBorder
                                                    studentAnswer={answerMap[q.id]}
                                                    partScoreById={partScoreById}
                                                    wholeQuestionScoreByQid={wholeQuestionScoreByQid}
                                                    choiceGroupSelections={choiceGroupSelections}
                                                    isAdmin={isAdmin}
                                                    blankIsPriced={blankIsPriced}
                                                    editingPart={editingPart}
                                                    setEditingPart={setEditingPart}
                                                    onSavePartScore={handleSavePartScore}
                                                />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>

                            {/* Vertical progress sidebar */}
                            <Box sx={{
                                width: 40, flexShrink: 0, display: 'flex', flexDirection: 'column',
                                alignItems: 'center', py: 1.5, gap: 0.5,
                                borderLeft: '1px solid', borderColor: colors.grey[700],
                            }}>
                                <IconButton size="small" disabled={isFirst} onClick={() => resultProgrammaticScroll(Math.max(0, currentQuestionIndex - 1))} sx={{ p: 0.5 }}>
                                    <NavigateBeforeIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />
                                </IconButton>
                                <Box sx={{ flex: 1, width: 8, borderRadius: 4, bgcolor: colors.grey[800], position: 'relative', overflow: 'visible', my: 0.5 }}>
                                    {/* Flag markers */}
                                    {curQuestions.map((q, idx) => answerMap[q.id]?.flagged ? (
                                        <FlagIcon key={q.id}
                                            onClick={(e) => { e.stopPropagation(); resultProgrammaticScroll(idx); }}
                                            sx={{
                                                position: 'absolute', top: `${((idx + 0.5) / curQuestions.length) * 100}%`, left: '100%',
                                                transform: 'translate(2px, -50%) rotate(90deg)',
                                                fontSize: 14, color: '#f59e0b', cursor: 'pointer', zIndex: 3,
                                            }}
                                        />
                                    ) : null)}
                                    {/* Segments — color-coded by correctness */}
                                    {curQuestions.map((q, idx) => {
                                        const sa = answerMap[q.id];
                                        const isMcq = ['mcq', 'multi_answer', 'grid_in', 'sa'].includes(q.question_type);
                                        let segColor;
                                        if (!sa?.answer_text) segColor = 'transparent';
                                        else if (isMcq) segColor = sa.is_correct ? '#4caf50' : sa.is_correct === false ? '#f44336' : colors.grey[600];
                                        else {
                                            const qParts = (partScores || []).filter(ps => ps.question_id === q.id);
                                            if (qParts.length === 0 || qParts.some(ps => ps.grading_status === 'pending')) segColor = colors.grey[600];
                                            else {
                                                const earned = qParts.reduce((s, ps) => s + (ps.score || 0), 0);
                                                const max = qParts.reduce((s, ps) => s + (ps.max_score || 0), 0);
                                                segColor = earned >= max ? '#4caf50' : earned > 0 ? '#f59e0b' : '#f44336';
                                            }
                                        }
                                        const segHeight = 100 / curQuestions.length;
                                        return (
                                            <Box key={q.id}
                                                onClick={() => resultProgrammaticScroll(idx)}
                                                sx={{
                                                    position: 'absolute', left: 0, right: 0,
                                                    top: `${idx * segHeight}%`, height: `${segHeight}%`,
                                                    bgcolor: segColor,
                                                    borderBottom: idx < curQuestions.length - 1 ? `0.5px solid ${colors.grey[900]}` : 'none',
                                                    cursor: 'pointer', opacity: 0.75, '&:hover': { opacity: 1 },
                                                    transition: 'background-color 0.15s',
                                                }}
                                            />
                                        );
                                    })}
                                    {/* Current marker — draggable */}
                                    <Box
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            resultProgrammaticUntil.current = Date.now() + 60000;
                                            const track = e.currentTarget.parentElement;
                                            const trackRect = track.getBoundingClientRect();
                                            const container = resultScrollRef.current;
                                            const doScroll = (clientY) => {
                                                const pct = Math.max(0, Math.min(1, (clientY - trackRect.top) / trackRect.height));
                                                const idx = Math.max(0, Math.min(curQuestions.length - 1, Math.floor(pct * curQuestions.length)));
                                                setCurrentQuestionIndex(idx);
                                                if (container) {
                                                    const maxScroll = container.scrollHeight - container.clientHeight;
                                                    container.scrollTop = pct * maxScroll;
                                                }
                                            };
                                            const onMove = (ev) => doScroll(ev.clientY);
                                            const onUp = () => {
                                                document.removeEventListener('mousemove', onMove);
                                                document.removeEventListener('mouseup', onUp);
                                                resultProgrammaticUntil.current = Date.now() + 100;
                                            };
                                            document.addEventListener('mousemove', onMove);
                                            document.addEventListener('mouseup', onUp);
                                        }}
                                        sx={{
                                            position: 'absolute', left: -4, right: -4,
                                            top: `${((currentQuestionIndex + 0.5) / curQuestions.length) * 100}%`,
                                            transform: 'translateY(-50%)',
                                            height: 12, bgcolor: 'text.primary', borderRadius: 1, zIndex: 2,
                                            cursor: 'grab', '&:active': { cursor: 'grabbing' },
                                            transition: 'top 0.15s ease-out',
                                        }}
                                    />
                                </Box>
                                <IconButton size="small" disabled={isLast} onClick={() => resultProgrammaticScroll(Math.min(curQuestions.length - 1, currentQuestionIndex + 1))} sx={{ p: 0.5 }}>
                                    <NavigateNextIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />
                                </IconButton>
                            </Box>
                        </Box>
                    )}
                </Box>
            ) : (
                /* ── PDF results mode (existing behavior, untouched) ── */
                <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                    <Box sx={{ flex: dividerPosition, position: 'relative' }} onContextMenu={(e) => e.preventDefault()}>
                        <Box sx={{
                            position: 'absolute', inset: 0, overflow: 'auto', p: 1,
                            userSelect: 'none', WebkitUserSelect: 'none',
                            visibility: showExplanations ? 'hidden' : 'visible',
                        }}>
                            {test?.file_url ? (
                                <Document file={test.file_url}
                                    onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                                    loading={<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>}
                                >
                                    {numPages && Array.from({ length: numPages }, (_, i) => (
                                        <Page key={i + 1} pageNumber={i + 1} scale={pixelRatio} renderTextLayer={false} renderAnnotationLayer={false} />
                                    ))}
                                </Document>
                            ) : (
                                <Typography p={4} color="text.secondary">No PDF available</Typography>
                            )}
                        </Box>
                        {test?.explanations_file_url && (
                            <Box sx={{
                                position: 'absolute', inset: 0, overflow: 'auto', p: 1,
                                userSelect: 'none', WebkitUserSelect: 'none',
                                visibility: showExplanations ? 'visible' : 'hidden',
                            }}>
                                <PdfDoc file={test.explanations_file_url} />
                            </Box>
                        )}
                    </Box>

                    {/* Resizer */}
                    <Box
                        onMouseDown={handleMouseDown}
                        sx={{
                            width: 8,
                            cursor: 'col-resize',
                            bgcolor: 'divider',
                            '&:hover': { bgcolor: colors.blueAccent[500] },
                            transition: 'background-color 0.15s',
                            flexShrink: 0,
                            zIndex: 10,
                            position: 'relative',
                        }}
                    />

                    <Box ref={rightPaneRef} sx={{ flex: 10 - dividerPosition, display: 'flex', flexDirection: 'column', minWidth: 300 }}>
                        <QuestionNav
                            sections={sections}
                            currentSectionIndex={currentSectionIndex}
                            onSectionNavigate={(idx) => { setCurrentSectionIndex(idx); setCurrentQuestionIndex(0); }}
                            answers={new Map(studentAnswers.map(a => [a.question_id, { answer_text: a.answer_text }]))}
                            viewMode={viewMode}
                            onToggleViewMode={() => { const next = viewMode === 'single' ? 'all' : 'single'; setViewMode(next); localStorage.setItem('v2ResultViewMode', next); }}
                            currentQuestionIndex={currentQuestionIndex}
                            sectionTotal={curQuestions.length}
                            isFirst={isFirst}
                            isLast={isLast}
                            onPrev={goPrev}
                            onNext={goNext}
                            onProgressClick={(idx) => setCurrentQuestionIndex(idx)}
                            flaggedQuestions={curQuestions
                                .map((q, idx) => answerMap[q.id]?.flagged ? { id: q.id, index: idx } : null)
                                .filter(Boolean)}
                            onFlagClick={(idx) => setCurrentQuestionIndex(idx)}
                            questionResults={curQuestions.map(q => {
                                const sa = answerMap[q.id];
                                const isMcq = ['mcq', 'multi_answer', 'grid_in', 'sa'].includes(q.question_type);
                                if (!sa?.answer_text) return { status: 'unanswered' };
                                if (isMcq) return { status: sa.is_correct === true ? 'correct' : sa.is_correct === false ? 'wrong' : 'pending' };
                                const qParts = (partScores || []).filter(ps => ps.question_id === q.id);
                                if (qParts.length === 0) return { status: 'pending' };
                                if (qParts.some(ps => ps.grading_status === 'pending' || ps.grading_status === 'grading')) return { status: 'pending' };
                                const earned = qParts.reduce((s, ps) => s + (ps.score || 0), 0);
                                const max = qParts.reduce((s, ps) => s + (ps.max_score || 0), 0);
                                if (earned >= max) return { status: 'correct' };
                                if (earned > 0) return { status: 'partial' };
                                return { status: 'wrong' };
                            })}
                        />
                        <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1 }}>
                            {curQuestions.length === 0 ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                    <Typography variant="body1" color="text.secondary">
                                        No questions in this section — use the section tabs above to continue.
                                    </Typography>
                                </Box>
                            ) : viewMode === 'single' ? (
                                curQuestions[currentQuestionIndex] && (
                                    <Box key={`${currentSectionIndex}-${currentQuestionIndex}`}
                                        sx={{ '@keyframes fadeSlideIn': { from: { opacity: 0, transform: 'translateX(12px)' }, to: { opacity: 1, transform: 'translateX(0)' } },
                                            animation: 'fadeSlideIn 0.15s ease-out' }}>
                                        <QuestionSlot
                                            question={curQuestions[currentQuestionIndex]}
                                            allQuestionsInSection={curQuestions}
                                            readOnly revealCorrectAnswers={revealCorrectAnswers}
                                            studentAnswer={answerMap[curQuestions[currentQuestionIndex].id]}
                                            partScoreById={partScoreById}
                                            wholeQuestionScoreByQid={wholeQuestionScoreByQid}
                                            choiceGroupSelections={choiceGroupSelections}
                                            isAdmin={isAdmin}
                                            blankIsPriced={blankIsPriced}
                                            editingPart={editingPart}
                                            setEditingPart={setEditingPart}
                                            onSavePartScore={handleSavePartScore}
                                        />
                                    </Box>
                                )
                            ) : (
                                curQuestions.map(q => (
                                    <QuestionSlot
                                        key={q.id}
                                        question={q}
                                        allQuestionsInSection={curQuestions}
                                        readOnly revealCorrectAnswers={revealCorrectAnswers}
                                        studentAnswer={answerMap[q.id]}
                                        partScoreById={partScoreById}
                                        wholeQuestionScoreByQid={wholeQuestionScoreByQid}
                                        choiceGroupSelections={choiceGroupSelections}
                                        isAdmin={isAdmin}
                                        blankIsPriced={blankIsPriced}
                                        editingPart={editingPart}
                                        setEditingPart={setEditingPart}
                                        onSavePartScore={handleSavePartScore}
                                    />
                                ))
                            )}
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default StudentResultV2;
